import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Input,
  Spinner,
  Form
} from "reactstrap";
import {
  Block,
  BlockBetween,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  Icon,
  PaginationComponent,
  PreviewAltCard,
  TooltipComponent
} from "../../../../components/Component";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import { fetchBlogKeywords, deleteBlogKeyword } from "../../../../api/blogs/keywords";
import { toast } from "react-toastify";
import AddKeywordModal from "./AddKeywordModal";
import BulkImportKeywordsModal from "./BulkImportKeywordsModal";
import ConfirmationModal from "../ConfirmationModal";

const KeywordsTable = () => {
  const axios = useAxiosPrivate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [addModal, setAddModal] = useState(false);
  const [bulkImportModal, setBulkImportModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [toDeleteId, setToDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [tablesm, updateTableSm] = useState(false);
  const [onSearch, setonSearch] = useState(true);
  const [filCount, setFilCount] = useState(0);

  // Load keywords
  const loadKeywords = async () => {
    setIsLoading(true);
    try {
      const keywords = await fetchBlogKeywords(axios);
      setData(keywords || []);
      setFilCount(keywords?.length || 0);
    } catch (error) {
      toast.error("Failed to load keywords");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadKeywords();
  }, []);

  // Filter data based on search input
  const filteredData = useMemo(() => {
    if (!searchText) return data;
    return data.filter(item =>
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (item.slug && item.slug.toLowerCase().includes(searchText.toLowerCase()))
    );
  }, [data, searchText]);

  // Pagination
  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle edit
  const handleEdit = (item) => {
    setEditData(item);
    setAddModal(true);
  };

  // Handle delete
  const handleDeleteClick = (id) => {
    setToDeleteId(id);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!toDeleteId) return;

    setDeleteLoading(true);
    try {
      await deleteBlogKeyword(axios, toDeleteId);
      toast.success("Keyword deleted successfully");
      loadKeywords();
    } catch (error) {
      toast.error(error.message || "Failed to delete keyword");
    } finally {
      setDeleteLoading(false);
      setDeleteModal(false);
      setToDeleteId(null);
    }
  };

  // Toggle modals
  const toggleAddModal = () => {
    setAddModal(!addModal);
    if (addModal) setEditData(null);
  };

  const toggleBulkImportModal = () => {
    setBulkImportModal(!bulkImportModal);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchText(e.target.value);
  };

  const toggleSearch = () => setonSearch(!onSearch);

  return (
    <React.Fragment>
      <Block>
        <DataTable className="card-stretch nk-tb-list">
          <div className="card-inner position-relative card-tools-toggle">
            <div className="card-title-group">
              <div className="card-tools">
                <div className="form-inline flex-nowrap gx-3">
                  <div className="form-wrap w-150px">
                    <Button color="primary" onClick={toggleAddModal}>
                      <Icon name="plus"></Icon><span>Add Keyword</span>
                    </Button>
                  </div>
                  <div className="form-wrap w-150px ms-3">
                    <Button color="primary" onClick={toggleBulkImportModal}>
                      <Icon name="upload"></Icon><span>Bulk Import</span>
                    </Button>
                  </div>
                </div>
              </div>
              <div className="card-tools me-n1">
                <ul className="btn-toolbar gx-1">
                  <li>
                    <a
                      href="#search"
                      onClick={(ev) => {
                        ev.preventDefault();
                        toggleSearch();
                      }}
                      className="btn btn-icon search-toggle toggle-search"
                    >
                      <Icon name="search"></Icon>
                    </a>
                  </li>
                  <li className="btn-toolbar-sep"></li>
                  <li>
                    <div className="toggle-wrap">
                      <Button
                        className={`btn-icon btn-trigger toggle ${tablesm ? "active" : ""}`}
                        onClick={() => updateTableSm(true)}
                      >
                        <Icon name="menu-right"></Icon>
                      </Button>
                      <div className={`toggle-content ${tablesm ? "content-active" : ""}`}>
                        <ul className="btn-toolbar gx-1">
                          <li className="toggle-close">
                            <Button
                              className="btn-icon btn-trigger toggle"
                              onClick={() => updateTableSm(false)}
                            >
                              <Icon name="arrow-left"></Icon>
                            </Button>
                          </li>
                          <li>
                            <UncontrolledDropdown>
                              <DropdownToggle tag="a" className="btn btn-trigger btn-icon dropdown-toggle">
                                <Icon name="setting"></Icon>
                              </DropdownToggle>
                              <DropdownMenu end className="dropdown-menu-xs">
                                <ul className="link-check">
                                  <li>
                                    <span>Show</span>
                                  </li>
                                  <li className={itemPerPage === 10 ? "active" : ""}>
                                    <DropdownItem
                                      tag="a"
                                      href="#dropdownitem"
                                      onClick={(ev) => {
                                        ev.preventDefault();
                                        setCurrentPage(1);
                                        setItemPerPage(10);
                                      }}
                                    >
                                      10
                                    </DropdownItem>
                                  </li>
                                  <li className={itemPerPage === 15 ? "active" : ""}>
                                    <DropdownItem
                                      tag="a"
                                      href="#dropdownitem"
                                      onClick={(ev) => {
                                        ev.preventDefault();
                                        setCurrentPage(1);
                                        setItemPerPage(15);
                                      }}
                                    >
                                      15
                                    </DropdownItem>
                                  </li>
                                  <li className={itemPerPage === 20 ? "active" : ""}>
                                    <DropdownItem
                                      tag="a"
                                      href="#dropdownitem"
                                      onClick={(ev) => {
                                        ev.preventDefault();
                                        setCurrentPage(1);
                                        setItemPerPage(20);
                                      }}
                                    >
                                      20
                                    </DropdownItem>
                                  </li>
                                </ul>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <div className={`card-search search-wrap ${!onSearch && "active"}`}>
              <div className="card-body">
                <div className="search-content">
                  <Button
                    className="search-back btn-icon toggle-search active"
                    onClick={() => {
                      setSearchText("");
                      toggleSearch();
                    }}
                  >
                    <Icon name="arrow-left"></Icon>
                  </Button>
                  <Input
                    type="text"
                    className="border-transparent form-focus-none"
                    placeholder="Search by keyword name or slug"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <Button className="search-submit btn-icon">
                    <Icon name="search"></Icon>
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <DataTableBody className="nk-tb-list is-separate is-medium">
            <DataTableHead className="nk-tb-item">
              <DataTableRow>
                <span className="sub-text">Name</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Slug</span>
              </DataTableRow>
              <DataTableRow className="nk-tb-col-tools text-end">
                <span className="sub-text">Actions</span>
              </DataTableRow>
            </DataTableHead>
            {isLoading ? (
              <DataTableItem>
                <DataTableRow className="text-center" style={{ width: "100%" }}>
                  <Spinner color="primary" />
                </DataTableRow>
              </DataTableItem>
            ) : (
              currentItems.map((item) => (
                <DataTableItem key={item.id} className="nk-tb-item">
                  <DataTableRow>
                    <span className="tb-lead">{item.name}</span>
                  </DataTableRow>
                  <DataTableRow>
                    <Badge color="light" className="text-dark">
                      {item.slug}
                    </Badge>
                  </DataTableRow>
                  <DataTableRow className="nk-tb-col-tools">
                    <ul className="nk-tb-actions gx-1">
                      <li>
                        <TooltipComponent
                          tag="a"
                          containerClassName="btn btn-trigger btn-icon"
                          id={`edit-${item.id}`}
                          icon="edit-alt-fill"
                          direction="top"
                          text="Edit"
                          onClick={(ev) => {
                            ev.preventDefault();
                            handleEdit(item);
                          }}
                        />
                      </li>
                      <li>
                        <TooltipComponent
                          tag="a"
                          containerClassName="btn btn-trigger btn-icon text-danger"
                          id={`delete-${item.id}`}
                          icon="trash-fill"
                          direction="top"
                          text="Delete"
                          onClick={(ev) => {
                            ev.preventDefault();
                            handleDeleteClick(item.id);
                          }}
                        />
                      </li>
                    </ul>
                  </DataTableRow>
                </DataTableItem>
              ))
            )}
          </DataTableBody>
          <div className="card-inner">
            {currentItems.length > 0 ? (
              <PaginationComponent
                itemPerPage={itemPerPage}
                totalItems={filteredData.length}
                paginate={paginate}
                currentPage={currentPage}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">No keywords found</span>
              </div>
            )}
          </div>
        </DataTable>
      </Block>

      {/* Add/Edit Keyword Modal */}
      <AddKeywordModal
        isOpen={addModal}
        toggle={toggleAddModal}
        onSuccess={loadKeywords}
        editData={editData}
      />

      {/* Bulk Import Modal */}
      <BulkImportKeywordsModal
        isOpen={bulkImportModal}
        toggle={toggleBulkImportModal}
        onSuccess={loadKeywords}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal}
        toggle={() => setDeleteModal(!deleteModal)}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title="Delete Keyword"
        message="Are you sure you want to delete this keyword? This action cannot be undone."
      />
    </React.Fragment>
  );
};

export default KeywordsTable;
